import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { AlertCircle, UserPlus, ArrowRight, Loader2 } from 'lucide-react'; 

const AddEmployeeModal = ({ user, onClose, onEmployeeAdded, initialPropertyId, onEnrollmentComplete }) => {
    const [addType, setAddType] = useState('invite'); // 'invite' or 'enroll'
    const [searchTerm, setSearchTerm] = useState('');
    
    // Unified Form Data
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Manager',
        properties: [],
    });

    const [isExistingUser, setIsExistingUser] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [availableProperties, setAvailableProperties] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Enrollment State ---
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');

    // --- Filter properties ---
    const filteredProperties = availableProperties.filter(prop => 
        prop.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const handleSelectAll = () => {
        const allIds = filteredProperties.map(prop => prop.id);
        setFormData(prev => ({ ...prev, properties: allIds }));
    };
    const handleDeselectAll = () => {
        setFormData(prev => ({ ...prev, properties: [] }));
    };

    // 1. Fetch properties (Invite Mode)
    useEffect(() => {
        const fetchUserProperties = async () => {
            try {
                const props = await api.getProperties();
                setAvailableProperties(props);
            } catch (error) {
                toast.error('Could not load properties list.');
            }
        };
        fetchUserProperties();
    }, []);

    // 2. Fetch Devices (Enroll Mode)
    useEffect(() => {
        if (addType === 'enroll' && initialPropertyId) {
            const fetchDevices = async () => {
                try {
                    const deviceList = await api.getDevices({ propertyId: initialPropertyId });
                    setDevices(deviceList);
                    if (deviceList.length > 0) {
                        setSelectedDeviceId(deviceList[0].id);
                    }
                } catch (error) {
                    console.error(error);
                    toast.error("Failed to load devices for enrollment.");
                }
            };
            fetchDevices();
        }
    }, [addType, initialPropertyId]);

    // 3. Email Lookup Logic (Invite Mode)
    useEffect(() => {
        const email = formData.email.trim();
        const delay = 500;
        if (!email) {
            setIsExistingUser(false);
            return;
        }
        const handler = setTimeout(async () => {
            setIsCheckingEmail(true);
            try {
                const existingUser = await api.getUserByEmail(email);
                setFormData(prev => ({
                    ...prev,
                    firstName: existingUser.first_name,
                    lastName: existingUser.last_name,
                    phone: existingUser.phone,
                    role: existingUser.role,
                }));
                setIsExistingUser(true);
                toast.success(`Existing user '${existingUser.first_name}' found.`);
            } catch (error) {
                if (error.statusCode === 404) {
                    if (isExistingUser) {
                        setFormData(prev => ({ ...prev, firstName: '', lastName: '', phone: '', role: 'Manager' }));
                    }
                    setIsExistingUser(false);
                } else {
                    toast.error(error.message || 'Failed to check email address.');
                }
            } finally {
                setIsCheckingEmail(false);
            }
        }, delay);
        return () => clearTimeout(handler);
    }, [formData.email, isExistingUser]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePropertyChange = (propertyId) => {
        setFormData(prev => {
            const newProperties = prev.properties.includes(propertyId)
                ? prev.properties.filter(id => id !== propertyId)
                : [...prev.properties, propertyId];
            return { ...prev, properties: newProperties };
        });
    };

    // --- HANDLE INVITE SUBMISSION ---
    const handleSubmitInvite = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (formData.properties.length === 0) {
            toast.error('At least one property must be assigned.');
            setIsSubmitting(false);
            return;
        }
        try {
            const response = await api.createAdminUser({ ...formData, isExistingUser });
            toast.success(response.message);
            onEmployeeAdded();
            onClose();
        } catch (error) {
            toast.error(error.message || 'Failed to process request.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- NEW: HANDLE ENROLLMENT SUBMISSION (Simplified) ---
    const handleCreateUser = async (e) => {
        e.preventDefault();
        
        if (!formData.firstName || !formData.lastName) {
            toast.error("First Name and Last Name are required.");
            return;
        }
        if (!selectedDeviceId) {
            toast.error("Please select a device.");
            return;
        }

        try {
            setIsSubmitting(true);
            
            // 1. Create User on Device & DB
            const response = await api.createEmployee(selectedDeviceId, { 
                firstName: formData.firstName, 
                lastName: formData.lastName 
            });
            
            toast.success("User created successfully!");
            
            // 2. Hand off to Parent (PropertiesScreen)
            // FIX: We send the data exactly as PropertiesScreen expects it (camelCase)
            if (onEnrollmentComplete) {
                const dataForParent = {
                    userId: response.userId,         // Matching PropertiesScreen expectation
                    employeeCode: response.employeeCode,
                    firstName: formData.firstName,   // Matching PropertiesScreen expectation
                    lastName: formData.lastName      // Matching PropertiesScreen expectation
                };
                onEnrollmentComplete(dataForParent);
            } else {
                onEmployeeAdded();
            }
            
            // 3. Close this modal
            onClose();

        } catch (error) {
            console.error("Creation Error:", error);
            const msg = error.message && error.message.includes('MenuProcessing') 
                ? 'Device menu is open. Please close it.' 
                : (error.message || "Failed to create user.");
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const canCreateOwner = user.role === 'SuperAdmin';

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
                <h2>Add a New User</h2>
                
                <div className="add-type-selector">
                    <button 
                        className={`selector-btn ${addType === 'enroll' ? 'active' : ''}`}
                        onClick={() => setAddType('enroll')}
                    >
                        Enroll Staff on Device
                    </button>

                    <button 
                        className={`selector-btn ${addType === 'invite' ? 'active' : ''}`}
                        onClick={() => setAddType('invite')}
                    >
                        Invite Owner or Manager
                    </button>
                </div>

                {/* --- INVITE FLOW (Unchanged) --- */}
                {addType === 'invite' ? (
                    <form onSubmit={handleSubmitInvite}>
                        <div className="form-group">
                            <label className="form-label required">Email Address</label>
                            <input type="email" name="email" className="form-input" value={formData.email} onChange={handleInputChange} required />
                            {isCheckingEmail && <small>Checking email...</small>}
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label required">First Name</label>
                                <input type="text" name="firstName" className="form-input" value={formData.firstName} onChange={handleInputChange} required readOnly={isExistingUser} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Last Name</label>
                                <input type="text" name="lastName" className="form-input" value={formData.lastName} onChange={handleInputChange} required readOnly={isExistingUser} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label required">Phone Number</label>
                                <input type="tel" name="phone" className="form-input" value={formData.phone} onChange={handleInputChange} required readOnly={isExistingUser} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Role</label>
                                <select name="role" className="form-input form-select" value={formData.role} onChange={handleInputChange} required disabled={isExistingUser}>
                                    {canCreateOwner && <option value="Owner">Owner</option>}
                                    <option value="Manager">Manager</option>
                                    <option value="Accountant">Accountant</option>
                                    {isExistingUser && <option value="Staff">Staff</option>}
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label required">Assign to Properties</label>
                            <div className="property-list-actions">
                                <input type="text" className="form-input search-input-small" placeholder="Filter properties..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
                                <div className="bulk-actions"><button type="button" className="action-link" onClick={handleSelectAll}>Select All</button><span>|</span><button type="button" className="action-link" onClick={handleDeselectAll}>Deselect All</button></div>
                            </div>
                            <div className="properties-checkbox-group">
                                {filteredProperties.map(prop => (
                                    <label key={prop.id} className="checkbox-label">
                                        <input type="checkbox" checked={formData.properties.includes(prop.id)} onChange={() => handlePropertyChange(prop.id)} />{prop.name}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="button button-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                            <button type="submit" className="button action-button" disabled={isSubmitting || isCheckingEmail}>
                                {isSubmitting ? (isExistingUser ? 'Assigning...' : 'Sending Invite...') : (isExistingUser ? 'Assign to Properties' : 'Send Invite')}
                            </button>
                        </div>
                    </form>
                ) : (
                    /* --- ENROLLMENT FLOW (Simplified) --- */
                    <div className="enroll-section" style={{ marginTop: '20px' }}>
                        {devices.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '30px', color: '#64748b', background: '#f8fafc', borderRadius: '8px' }}>
                                <AlertCircle size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
                                <p>No active devices found for this property.</p>
                                <small>Please add a device in the "Devices" tab first.</small>
                                <div className="modal-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
                                    <button type="button" className="button button-secondary" onClick={onClose}>Close</button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleCreateUser}>
                                <div className="form-group">
                                    <label className="form-label">Select Device for Creation</label>
                                    <select 
                                        className="form-input" 
                                        value={selectedDeviceId} 
                                        onChange={(e) => setSelectedDeviceId(e.target.value)}
                                    >
                                        {devices.map(d => (
                                            <option key={d.id} value={d.id}>{d.name} ({d.serial_number})</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label required">First Name</label>
                                        <input 
                                            type="text" 
                                            name="firstName" 
                                            className="form-input" 
                                            value={formData.firstName} 
                                            onChange={handleInputChange} 
                                            placeholder="e.g. John"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label required">Last Name</label>
                                        <input 
                                            type="text" 
                                            name="lastName" 
                                            className="form-input" 
                                            value={formData.lastName} 
                                            onChange={handleInputChange} 
                                            placeholder="e.g. Doe"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="info-box" style={{ background: '#f0f9ff', padding: '15px', borderRadius: '8px', border: '1px solid #bae6fd', color: '#0369a1', fontSize: '0.9rem', marginBottom: '20px', display:'flex', gap:'12px', alignItems:'flex-start' }}>
                                    <UserPlus size={20} style={{ minWidth:'20px', marginTop:'2px' }} />
                                    <div>
                                        <strong>Create & Configure</strong>
                                        <p style={{ margin: '4px 0 0 0', opacity: 0.9 }}>
                                            Creating the user will add them to the device immediately. You will then be automatically redirected to their profile to set up <strong>Passcode, Face ID, or Fingerprint</strong>.
                                        </p>
                                    </div>
                                </div>

                                <div className="modal-actions" style={{ marginTop: '25px' }}>
                                    <button type="button" className="button button-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                                    <button 
                                        type="submit" 
                                        className="button action-button" 
                                        disabled={isSubmitting || !formData.firstName || !formData.lastName}
                                        style={{ minWidth: '160px' }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="spin" size={18} style={{ marginRight: '8px' }} /> Creating...
                                            </>
                                        ) : (
                                            <>
                                                Create User <ArrowRight size={18} style={{ marginLeft: '8px', opacity: 0.8 }} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddEmployeeModal;